import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Mail, Trash2, Send, CheckCircle2, AlertTriangle, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailPanel({ onClose, token }) {
  const [cfg, setCfg] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const [sending, setSending] = useState(false);
  const authCfg = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/email/config`, { headers: { Authorization: `Bearer ${token}` } });
    setCfg(res.data);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const addRecipient = async () => {
    if (!newEmail.includes("@")) return;
    const res = await axios.post(`${API}/email/recipients`, { email: newEmail }, authCfg);
    if (res.data.ok) { setNewEmail(""); load(); }
  };
  const removeRecipient = async (email) => {
    await axios.delete(`${API}/email/recipients/${encodeURIComponent(email)}`, authCfg);
    load();
  };
  const sendNow = async () => {
    setSending(true); setMsg(null);
    try {
      const res = await axios.post(`${API}/email/send-now`, {}, authCfg);
      if (res.data.error) setMsg({ type: "err", text: res.data.error });
      else setMsg({ type: "ok", text: `Sent to ${res.data.sent}/${res.data.recipients} recipient(s).` });
    } catch (e) {
      setMsg({ type: "err", text: "Send failed. Check backend logs." });
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-[#0B2830]/40 flex items-center justify-center z-50 p-4" data-testid="email-panel">
      <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-xl w-full max-w-lg fade-up">
        <div className="flex items-center justify-between border-b border-[#D2E0E6] px-5 py-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#0284C7]" />
            <h3 className="font-semibold text-[#0B2830]">Daily emailed screen</h3>
          </div>
          <button data-testid="email-close" onClick={onClose} className="text-[#7C949C] hover:text-[#0B2830]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center gap-2 text-sm">
            <CalendarClock className="w-4 h-4 text-[#47616B]" />
            <span className="text-[#47616B]">Schedule:</span>
            <span className="mono text-[#0B2830]">{cfg?.schedule || "\u2014"}</span>
          </div>

          {cfg && (
            <div className={`flex items-start gap-2 text-sm border rounded-md px-3 py-2.5 ${
              cfg.configured ? "border-[#0D9488]/30 bg-[#0D9488]/5" : "border-[#D97706]/30 bg-[#D97706]/5"
            }`} data-testid="email-status">
              {cfg.configured ? <CheckCircle2 className="w-4 h-4 text-[#0D9488] mt-0.5" />
                : <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5" />}
              <span className={cfg.configured ? "text-[#0D9488]" : "text-[#B45309]"}>
                {cfg.configured
                  ? `Resend active. Sending from ${cfg.sender}.`
                  : "Resend not configured yet. Emails are queued but won't send until the API key + verified sender are added to the backend."}
              </span>
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-2">Recipients</label>
            <div className="space-y-1.5 mb-3">
              {(cfg?.recipients || []).map((r) => (
                <div key={r} data-testid={`recipient-${r}`} className="flex items-center justify-between bg-[#E9F1F4] border border-[#D2E0E6] rounded-md px-3 py-2">
                  <span className="mono text-sm text-[#0B2830]">{r}</span>
                  <button onClick={() => removeRecipient(r)} className="text-[#7C949C] hover:text-[#E11D48]" data-testid={`remove-${r}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {cfg && cfg.recipients.length === 0 && (
                <div className="text-xs text-[#7C949C]">No recipients yet.</div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="recipient-input"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                placeholder="name@email.com"
                className="flex-1 bg-white border border-[#D2E0E6] text-[#0B2830] mono text-sm px-3 py-2 rounded-md focus:border-[#0284C7] outline-none"
              />
              <button data-testid="add-recipient" onClick={addRecipient}
                className="bg-transparent border border-[#D2E0E6] text-[#47616B] px-3 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830]">
                Add
              </button>
            </div>
          </div>

          {msg && (
            <div data-testid="email-send-msg" className={`text-sm px-3 py-2 border rounded-md ${
              msg.type === "ok" ? "border-[#0D9488]/30 text-[#0D9488] bg-[#0D9488]/5"
                : "border-[#E11D48]/30 text-[#E11D48] bg-[#E11D48]/5"}`}>
              {msg.text}
            </div>
          )}

          <button data-testid="send-now" onClick={sendNow} disabled={sending}
            className="w-full flex items-center justify-center gap-2 bg-[#0284C7] text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors disabled:opacity-50">
            <Send className="w-4 h-4" /> {sending ? "Sending\u2026" : "Send now (test)"}
          </button>
        </div>
      </div>
    </div>
  );
}
